import React, { useState, useEffect } from 'react';
import { Lightbulb, ArrowRight, Brain, CheckCircle, AlertCircle, Shuffle, Eye, GripVertical, BarChart3, BarChart2, Edit3 } from 'lucide-react';

// Silenciar logs de EPL por defecto (no afecta a Vertex).
// Activar explícitamente con: VITE_EPL_VERBOSE=true
const _eplConsole = globalThis.console;
// eslint-disable-next-line no-shadow
const console = import.meta.env.VITE_EPL_VERBOSE === 'true'
    ? _eplConsole
    : { ..._eplConsole, log: () => {}, info: () => {}, warn: () => {} };

const BMLLearnStep = ({ onValidateIA, initialData = null, simulatedData = null, isActive = false }) => {
    // 🧭 Estado del nuevo LEARN dinámico v2.0
    const [formData, setFormData] = useState({
        insights: '',
        decisiones_posibles: [''],
        proxima_hipotesis: ''
    });
    
    const [validation, setValidation] = useState({
        insights: { valid: false, message: '' },
        decisiones_posibles: { valid: false, message: '' },
        proxima_hipotesis: { valid: false, message: '' }
    });

    // 🎭 Estados para experiencia conversacional
    const [selectedInsight, setSelectedInsight] = useState(null);
    const [selectedDecision, setSelectedDecision] = useState(null);
    
    // 🧠 IA sugerencias dinámicas
    const [aiSuggestions, setAiSuggestions] = useState({
        insights: [],
        decisions: [],
        hypothesis: ''
    });

    // 🧠 Generar sugerencias IA dinámicas basadas en datos del experimento
    const generateAISuggestions = () => {
        // 💡 Insights sugeridos por IA (basados en resultados)
        const insights = [
            "El problema radica en la falta de conocimiento sobre el uso de herramientas de filtrado avanzado.",
            "Los usuarios confunden funciones clave con opciones secundarias en la interfaz.",
            "La funcionalidad es adecuada, pero el flujo de navegación genera fricción cognitiva."
        ];
        
        // 🧭 Decisiones sugeridas con contexto del experimento
        const decisions = [
            { text: "Agregar tutorial guiado", type: "operativa", color: "blue" },
            { text: "Renombrar etiquetas de filtros", type: "mvp", color: "green" },
            { text: "Cambiar segmento de usuarios", type: "estrategica", color: "orange" },
            { text: "Rediseñar flujo de navegación", type: "operativa", color: "blue" },
            { text: "Crear onboarding interactivo", type: "mvp", color: "green" }
        ];
        
        // 🔄 Hipótesis evolutiva sugerida
        const hypothesis = simulatedData?.hypothesis_text 
            ? `Creemos que jóvenes universitarios necesitan acompañamiento para aprender herramientas tecnológicas, porque se sienten inseguros frente a plataformas nuevas.`
            : `Creemos que estudiantes de ingeniería necesitan filtros más intuitivos para encontrar prácticas relevantes, porque las opciones actuales generan confusión cognitiva.`;
            
        setAiSuggestions({ insights, decisions, hypothesis });
    };



    // 💾 Cargar datos iniciales y generar sugerencias IA
    useEffect(() => {
        if (initialData && typeof initialData === 'object') {
            console.log('[💾 BMLLearnPhase] Cargando datos iniciales:', initialData);
            setFormData(prevData => ({
                ...prevData,
                ...initialData
            }));
        }
        
        // Generar sugerencias IA al cargar
        generateAISuggestions();
    }, [initialData, simulatedData]);

    // 🎯 Validación en tiempo real
    useEffect(() => {
        const validDecisiones = formData.decisiones_posibles.filter(d => d.trim().length > 0);
        
        setValidation({
            insights: {
                valid: formData.insights.length >= 60,
                message: formData.insights.length >= 60
                    ? '✅ Insight validado'
                    : `💡 Describe tu aprendizaje clave (${formData.insights.length}/60 caracteres)`
            },
            decisiones_posibles: {
                valid: validDecisiones.length >= 1,
                message: validDecisiones.length >= 1
                    ? '✅ Decisiones definidas'
                    : '🎯 Define al menos una decisión basada en tus datos'
            },
            proxima_hipotesis: {
                valid: formData.proxima_hipotesis.length >= 40,
                message: formData.proxima_hipotesis.length >= 40
                    ? '✅ Nueva hipótesis evolutiva lista'
                    : `🔄 Define tu próxima hipótesis (${formData.proxima_hipotesis.length}/40)`
            }
        });
    }, [formData]);

    const isFormComplete = Object.values(validation).every(v => v.valid);

    // Funciones para manejo de decisiones
    const addDecision = () => {
        if (formData.decisiones_posibles.length < 3) {
            setFormData(prev => ({
                ...prev,
                decisiones_posibles: [...prev.decisiones_posibles, '']
            }));
        }
    };

    const removeDecision = (index) => {
        if (formData.decisiones_posibles.length > 1) {
            setFormData(prev => ({
                ...prev,
                decisiones_posibles: prev.decisiones_posibles.filter((_, i) => i !== index)
            }));
        }
    };

    const handleValidateIA = async () => {
        if (isFormComplete && onValidateIA) {
            // 🔧 Solo sistema IA externo - consistente con todo el Lab
            const learnInput = `LEARN Phase - Business Model Lab:

📝 INSIGHTS CLAVE:
${formData.insights}

🎯 DECISIONES BASADAS EN DATOS:
${formData.decisiones_posibles.filter(d => d.trim()).map((decision, i) => `${i + 1}. ${decision}`).join('\n')}

🔄 NUEVA HIPÓTESIS EVOLUTIVA:
${formData.proxima_hipotesis}`.trim();

            onValidateIA(learnInput, 'bml_learn', formData);
        }
    };

    if (!isActive) return null;

    return (
        <div className="bg-white rounded-xl shadow-lg border border-purple-200 overflow-hidden">
            {/* 🎨 Header rediseñado v2.0 */}
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 text-white">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                        <Brain className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold">LEARN: Aprende y decide</h3>
                        <p className="text-purple-100">Tu cierre inteligente del ciclo Lean Startup</p>
                    </div>
                </div>
            </div>

            <div className="p-6 space-y-8" style={{ backgroundColor: '#F9FAFB' }}>
                
                {/* 1️⃣ RESUMEN INTELIGENTE DEL EXPERIMENTO */}
                {simulatedData && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <BarChart2 className="w-5 h-5 text-blue-600" />
                            📊 Resultados del experimento anterior
                        </h4>
                        
                        {/* Snapshot cards v2.3 */}
                        <div className="space-y-3">
                            {simulatedData.kpis?.map((kpi, index) => (
                                <div key={index} className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-400">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <span className="font-medium text-gray-800">{kpi.name}</span>
                                            <div className="text-2xl font-bold text-gray-900 mt-1">
                                                {kpi.value}{kpi.unit}
                                            </div>
                                        </div>
                                        <div className={`px-2 py-1 rounded text-xs font-medium ${
                                            kpi.confidence > 0.85 ? 'bg-green-100 text-green-700' :
                                            kpi.confidence > 0.7 ? 'bg-yellow-100 text-yellow-700' :
                                            'bg-red-100 text-red-700'
                                        }`}>
                                            {kpi.confidence > 0.85 ? '🟢 Alta' :
                                             kpi.confidence > 0.7 ? '🟡 Media' : '🔴 Baja'} confianza
                                        </div>
                                    </div>
                                    <div className="text-sm text-gray-600 italic">
                                        🧠 IA: "{simulatedData.interpretacion_kpi?.find(i => i.name === kpi.name)?.interpretacion_ia || 
                                              'Los usuarios muestran interés, pero necesitan mayor claridad en la propuesta.'}"
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                            <div className="text-sm text-blue-700">
                                📅 Experimento: {simulatedData.universe?.period_days || 7} días · 
                                👥 {simulatedData.universe?.n_users || 0} participantes
                            </div>
                        </div>
                    </div>
                )}

                {/* 2️⃣ INSIGHT GUIADO */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <Lightbulb className="w-5 h-5 text-yellow-600" />
                        <h4 className="font-semibold text-gray-800">💡 Insights sugeridos por IA</h4>
                    </div>
                    
                    {/* Sugerencias IA */}
                    {aiSuggestions.insights.length > 0 && (
                        <div className="mb-4 space-y-3">
                            {aiSuggestions.insights.map((insight, index) => (
                                <div 
                                    key={index}
                                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                        selectedInsight === index 
                                            ? 'border-blue-500 bg-blue-50' 
                                            : 'border-gray-200 bg-gray-50 hover:border-blue-300'
                                    }`}
                                    onClick={() => {
                                        setSelectedInsight(index);
                                        setFormData(prev => ({ ...prev, insights: insight }));
                                    }}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm flex items-center justify-center font-medium">
                                            {index + 1}
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm text-gray-700">{insight}</p>
                                        </div>
                                        {selectedInsight === index && (
                                            <CheckCircle className="w-5 h-5 text-blue-600" />
                                        )}
                                    </div>
                                </div>
                            ))}
                            
                            <div className="flex gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setSelectedInsight('custom')}
                                    className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
                                >
                                    ✏️ Escribir propio
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectedInsight(null);
                                        setFormData(prev => ({ ...prev, insights: '' }));
                                    }}
                                    className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                                >
                                    🗑️ Limpiar
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Campo de texto personalizado */}
                    {(selectedInsight === 'custom' || selectedInsight === null) && (
                        <div className="space-y-3">
                            <div className="relative">
                                <Brain className="absolute left-3 top-4 w-5 h-5 text-gray-400" />
                                <textarea
                                    value={formData.insights}
                                    onChange={(e) => setFormData(prev => ({ ...prev, insights: e.target.value }))}
                                    placeholder="Describe tus insights del experimento. ¿Qué patrones observaste? ¿Qué confirma o cuestiona tu hipótesis inicial?"
                                    className="w-full pl-10 pr-4 py-3 min-h-[120px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                                />
                            </div>
                            <div className={`text-xs px-2 py-1 rounded ${
                                validation.insights.valid 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-amber-100 text-amber-700'
                            }`}>
                                {validation.insights.message}
                            </div>
                        </div>
                    )}
                </div>

                {/* 3️⃣ DECISIONES SUGERIDAS */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <Shuffle className="w-5 h-5 text-green-600" />
                        <h4 className="font-semibold text-gray-800">🧭 Decisiones basadas en datos</h4>
                    </div>

                    {/* Decisiones sugeridas por IA */}
                    {aiSuggestions.decisions.length > 0 && (
                        <div className="mb-4">
                            <p className="text-sm text-gray-600 mb-3">💡 Sugerencias IA (haz clic para agregar):</p>
                            <div className="flex flex-wrap gap-2 mb-4">
                                {aiSuggestions.decisions.map((decision, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => {
                                            if (formData.decisiones_posibles.length < 3 && !formData.decisiones_posibles.includes(decision.text)) {
                                                const newDecisions = [...formData.decisiones_posibles.filter(d => d.trim()), decision.text];
                                                if (newDecisions.length < 3) newDecisions.push('');
                                                setFormData(prev => ({ ...prev, decisiones_posibles: newDecisions }));
                                            }
                                        }}
                                        className={`px-3 py-1 text-xs rounded-full border-2 transition-colors ${
                                            decision.color === 'blue' ? 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100' :
                                            decision.color === 'green' ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100' :
                                            'border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100'
                                        }`}
                                    >
                                        {decision.text}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Decisiones del usuario */}
                    <div className="space-y-3">
                        {formData.decisiones_posibles.map((decision, index) => (
                            <div key={index} className="flex gap-2 items-center">
                                <input
                                    type="text"
                                    value={decision}
                                    onChange={(e) => {
                                        const newDecisions = [...formData.decisiones_posibles];
                                        newDecisions[index] = e.target.value;
                                        setFormData(prev => ({ ...prev, decisiones_posibles: newDecisions }));
                                    }}
                                    placeholder={`Decisión ${index + 1} basada en tus datos...`}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                />
                                <button
                                    type="button"
                                    onClick={() => removeDecision(index)}
                                    className="px-2 py-2 text-red-600 hover:bg-red-50 rounded"
                                    disabled={formData.decisiones_posibles.length === 1}
                                >
                                    🗑️
                                </button>
                            </div>
                        ))}
                        
                        {formData.decisiones_posibles.length < 3 && (
                            <button
                                type="button"
                                onClick={addDecision}
                                className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-purple-300 hover:text-purple-600 transition-colors"
                            >
                                + Agregar decisión
                            </button>
                        )}
                        
                        <div className={`text-xs px-2 py-1 rounded ${
                            validation.decisiones_posibles.valid 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-amber-100 text-amber-700'
                        }`}>
                            {validation.decisiones_posibles.message}
                        </div>
                    </div>
                </div>

                {/* 4️⃣ NUEVA HIPÓTESIS EVOLUTIVA - VALIDACIÓN FINAL */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <Edit3 className="w-5 h-5 text-purple-600" />
                        <h4 className="font-semibold text-gray-800">🔄 Nueva hipótesis evolutiva</h4>
                    </div>

                    {/* Hipótesis sugerida por IA */}
                    {aiSuggestions.hypothesis && (
                        <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                            <p className="text-sm text-purple-800 font-medium mb-2">💬 IA sugiere:</p>
                            <p className="text-sm text-purple-700 italic mb-3">"{aiSuggestions.hypothesis}"</p>
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, proxima_hipotesis: aiSuggestions.hypothesis }))}
                                className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 transition-colors"
                            >
                                ✅ Usar como base
                            </button>
                        </div>
                    )}

                    <div className="space-y-3">
                        <div className="relative">
                            <ArrowRight className="absolute left-3 top-4 w-5 h-5 text-gray-400" />
                            <textarea
                                value={formData.proxima_hipotesis}
                                onChange={(e) => setFormData(prev => ({ ...prev, proxima_hipotesis: e.target.value }))}
                                placeholder="Reformula tu hipótesis: 'Creemos que [grupo] tienen [problema] porque [razón]'. ¿Qué cambió respecto a la anterior?"
                                className="w-full pl-10 pr-4 py-3 min-h-[100px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                            />
                        </div>
                        <div className={`text-xs px-2 py-1 rounded ${
                            validation.proxima_hipotesis.valid 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-amber-100 text-amber-700'
                        }`}>
                            {validation.proxima_hipotesis.message}
                        </div>
                    </div>
                </div>



                {/* Botón de validación final */}
                <div className="text-center">
                    <button
                        onClick={handleValidateIA}
                        disabled={!isFormComplete}
                        className={`px-8 py-4 rounded-xl font-semibold transition-all duration-200 ${
                            isFormComplete
                                ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:shadow-lg transform hover:-translate-y-0.5'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        {isFormComplete ? (
                            <>
                                <Brain className="w-5 h-5 inline mr-2" />
                                🧠 Validar Aprendizaje con IA
                            </>
                        ) : (
                            <>
                                <Lightbulb className="w-5 h-5 inline mr-2" />
                                Completa tu reflexión para continuar
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BMLLearnStep;